import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Calendar, Plus, Trash2, Loader2 } from 'lucide-react';

const API_URL = window.location.origin;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const EMPTY = { title: '', description: '', date: '', time: '19:00', duration_minutes: 60, zoom_link: '', location: '' };

export const EventsAdmin = () => {
  const [events, setEvents] = useState({ upcoming: [], past: [] });
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/legacy/events`, authHeaders());
      setEvents(res.data);
    } catch (error) {
      console.error('Failed to load events:', error);
    }
  };

  useEffect(() => { fetchEvents(); }, []);

  const handleCreate = async () => {
    if (!form.title.trim() || !form.date) { toast.error('Title and date are required'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/legacy/events`, { ...form, duration_minutes: Number(form.duration_minutes) || 60 }, authHeaders());
      toast.success(`Event created — ${res.data.notified} member(s) notified 📅`);
      setForm(EMPTY);
      setShowForm(false);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event? Members will no longer see it or receive reminders.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/legacy/events/${id}`, authHeaders());
      toast.success('Event deleted');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  return (
    <Card className="card-base" data-testid="admin-legacy-events">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
          <Calendar className="h-5 w-5 text-terracotta" /> Legacy Calendar
        </CardTitle>
        <Button size="sm" onClick={() => setShowForm(!showForm)} className="bg-forest hover:bg-forest/90 text-white" data-testid="admin-event-toggle-form">
          <Plus className="h-4 w-4 mr-2" /> New Event
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-500 -mt-2">Members get reminders 7 days, 24 hours, and 1 hour before each event.</p>

        {showForm && (
          <div className="space-y-3 border border-sand rounded-xl p-4">
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Event title *" data-testid="admin-event-title" />
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (optional)" rows={2} data-testid="admin-event-description" />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="admin-event-date" />
              <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="admin-event-time" />
              <Input type="number" min="15" max="480" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} placeholder="Minutes" data-testid="admin-event-duration" />
            </div>
            <p className="text-xs text-gray-400 -mt-1">Time is Eastern Time (ET)</p>
            <Input value={form.zoom_link} onChange={(e) => setForm({ ...form, zoom_link: e.target.value })} placeholder="Zoom link (optional)" data-testid="admin-event-zoom" />
            <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location (optional, for in-person)" data-testid="admin-event-location" />
            <div className="flex justify-end">
              <Button onClick={handleCreate} disabled={saving} className="bg-forest hover:bg-forest/90 text-white" data-testid="admin-event-create-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />} Create &amp; Notify Members
              </Button>
            </div>
          </div>
        )}

        {events.upcoming.length > 0 ? (
          <div className="space-y-2">
            {events.upcoming.map(e => (
              <div key={e.id} className="flex items-center justify-between gap-3 border border-sand rounded-lg p-3" data-testid={`admin-event-${e.id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-forest truncate">{e.title}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(e.starts_at).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} · {e.time} {e.timezone_label} · {e.duration_minutes} min
                    {(e.reminders_sent || []).length > 0 && <Badge variant="outline" className="ml-2 text-xs">reminded: {(e.reminders_sent || []).join(', ')}</Badge>}
                  </p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(e.id)} data-testid={`admin-event-delete-${e.id}`}>
                  <Trash2 className="h-4 w-4 text-gray-400 hover:text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 text-center py-3">No upcoming events. Create one above.</p>
        )}
      </CardContent>
    </Card>
  );
};

export default EventsAdmin;
